// Visor de PDF compartido — dibuja cada página en `<canvas>` con `pdfjs-dist` en vez de un
// `<iframe src={pdfUrl}>`. Un iframe apuntando directo a un PDF deja la decisión "mostrar inline
// vs. descargar" en manos de la preferencia de PDF del navegador (`chrome://settings/content/
// pdfDocuments`) o de una política corporativa (`AlwaysOpenPdfExternally`) — con "Descargar
// archivos PDF" activado, CUALQUIER iframe apuntando a un PDF dispara la descarga en vez de
// mostrarlo, sin que la app pueda evitarlo desde el HTML (probado: ni siquiera sirviéndolo como
// `blob:` cambia el comportamiento, porque el navegador igual reconoce el `Content-Type`). Con
// pdf.js el navegador nunca ve un recurso PDF nativo: los bytes los consume la librería (WASM/JS)
// y el resultado son imágenes en un `<canvas>`, así que el visor se ve igual para todos los
// usuarios sin importar esa configuración. Usado por `CampaignDocs.tsx` (documentos de campaña) y
// `HomeRightRail.tsx` (popup de Legales/Instructivo).
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** Resolución objetivo del canvas — más nítido que el ancho visible del contenedor (hasta 2x en
 *  pantallas de alta densidad), para que no se vea borroso al agrandar la ventana. */
const RENDER_DPR_CAP = 2;

/**
 * `pdfjs-dist` (~1.2MB, worker incluido) se importa DINÁMICO, no al inicio del archivo: este
 * visor lo monta `HomeRightRail.tsx`, que a su vez vive en `AppLayout` y se monta en TODAS las
 * rutas `/app/*` — un `import` estático arrastraba la librería entera al bundle principal para
 * el 100% de las cargas de página, aunque casi nadie abre un documento en cada sesión. Con
 * `import()` queda en su propio chunk, descargado solo la primera vez que se abre un visor.
 * `pdfjsLibPromise` cachea esa carga para no repetirla en cada `<PdfCanvasViewer>` que se monte.
 */
let pdfjsLibPromise: ReturnType<typeof loadPdfjs> | null = null;
async function loadPdfjs() {
  const [pdfjsLib, { default: workerUrl }] = await Promise.all([
    import('pdfjs-dist'),
    import('pdfjs-dist/build/pdf.worker.min.mjs?url'),
  ]);
  pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;
  return pdfjsLib;
}

interface PdfCanvasViewerProps {
  url: string;
  onError: () => void;
  /** Clases del contenedor con scroll y del wrapper de páginas — permite reusar el estilo de
   *  cada consumidor (`.pdf-canvas-scroll`/`-pages` en el modal de documentos de campaña, etc.). */
  scrollClassName?: string;
  pagesClassName?: string;
  loadingClassName?: string;
  /** Se invoca cuando TODAS las páginas están pintadas. Existe porque `isLoading` es estado interno
   *  y el padre no puede saber cuándo el contenido ya ocupa su tamaño final: el modal legal habilita
   *  «Acepto» si el documento cabe sin scroll, y midiendo mientras el PDF se pinta el contenedor
   *  todavía mide poco — se habilitaría sin haber leído nada. Opcional: los consumidores que no la
   *  pasan no cambian en nada. */
  onLoaded?: () => void;
}

/** Dibuja cada página del PDF en un `<canvas>` propio, apiladas verticalmente y con scroll — sin
 *  paginación: el pedido explícito era "verse completo". El ancho se fija en el montaje y el
 *  propio `<canvas>` escala por CSS (`width:100%`), así que sigue respondiendo al tamaño del
 *  contenedor sin tener que re-renderizar el PDF en cada resize. */
export function PdfCanvasViewer({
  url,
  onError,
  scrollClassName = 'pdf-canvas-scroll',
  pagesClassName = 'pdf-canvas-pages',
  loadingClassName = 'pdf-modal-loading',
  onLoaded,
}: Readonly<PdfCanvasViewerProps>) {
  const { t } = useTranslation('recognition');
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  // `onError` suele llegar como una arrow function nueva en cada render del padre (p.ej.
  // `onError={() => setLoadError(true)}`) — si entrara en el array de dependencias, CUALQUIER
  // re-render del padre (por un motivo ajeno al documento, como un resize que dispara otro hook)
  // reiniciaba el fetch + render del PDF desde cero. Guardarla en un ref deja el efecto atado
  // solo a `url`, que es lo único que realmente debe disparar una nueva carga.
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  // Mismo motivo que `onErrorRef`: si `onLoaded` entrara en las dependencias, una arrow nueva del
  // padre reiniciaría el fetch + render del PDF desde cero.
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  useEffect(() => {
    let cancelled = false;
    let destroyDoc: (() => void) | null = null;
    const renderTasks: Array<{ cancel: () => void }> = [];
    const container = containerRef.current;
    setIsLoading(true);
    if (container) container.replaceChildren();

    (async () => {
      pdfjsLibPromise ??= loadPdfjs();
      const pdfjsLib = await pdfjsLibPromise;
      if (cancelled || !container) return;
      const loadingTask = pdfjsLib.getDocument({ url });
      destroyDoc = () => loadingTask.destroy();
      const pdf = await loadingTask.promise;
      if (cancelled) return;
      const targetWidth = Math.max(container.clientWidth, 320) * Math.min(window.devicePixelRatio || 1, RENDER_DPR_CAP);
      for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
        if (cancelled) return;
        const page = await pdf.getPage(pageNumber);
        const baseViewport = page.getViewport({ scale: 1 });
        const viewport = page.getViewport({ scale: targetWidth / baseViewport.width });
        const canvas = document.createElement('canvas');
        canvas.className = 'pdf-canvas-page';
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        container.appendChild(canvas);
        const canvasContext = canvas.getContext('2d');
        if (!canvasContext) continue;
        const task = page.render({ canvas, canvasContext, viewport });
        renderTasks.push(task);
        await task.promise;
      }
      if (!cancelled) {
        setIsLoading(false);
        onLoadedRef.current?.();
      }
    })().catch(() => { if (!cancelled) onErrorRef.current(); });

    return () => {
      cancelled = true;
      renderTasks.forEach((task) => task.cancel());
      destroyDoc?.();
    };
  }, [url]);

  return (
    <div className={scrollClassName}>
      {isLoading && <p className={loadingClassName} role="status">{t('common.loading')}</p>}
      <div ref={containerRef} className={pagesClassName} />
    </div>
  );
}

export default PdfCanvasViewer;
